<script setup lang="ts">
/**
 * An annex note's number and title.
 *
 * The number is typed rather than counted, because annex numbering is cited:
 * "note 12" has to stay note 12 when a note is added before it, and an
 * automatic count would renumber the whole annex under somebody's feet.
 */
import { useI18n } from 'vue-i18n'

import InspectorField from '@/sections/InspectorField.vue'
import type { NoteSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: NoteSection }>()
const emit = defineEmits<{ change: [section: NoteSection] }>()

function set(changes: Partial<NoteSection>): void {
  emit('change', { ...props.section, ...changes })
}
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.note.number')" :hint="t('inspector.note.numberHint')">
      <input
        class="b-input b-input--short"
        :value="section.number"
        placeholder="12"
        @input="set({ number: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <InspectorField :label="t('inspector.note.title')">
      <input
        class="b-input"
        :value="section.title"
        @input="set({ title: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <p class="note">{{ t('inspector.note.bodyOnPage') }}</p>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.b-input--short {
  width: 90px;
  font-variant-numeric: tabular-nums;
}

.note {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.5;
}
</style>
